import { useState, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { trpc } from '../../lib/trpc';

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

interface TouchedFields {
  password: boolean;
  confirmPassword: boolean;
}

/**
 * Reset password page component
 * Handles password reset using token from URL
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({
    password: false,
    confirmPassword: false,
  });
  const [success, setSuccess] = useState(false);

  const resetPasswordMutation = trpc.auth.resetPassword.useMutation({
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (err) => {
      setError(err.message || 'Failed to reset password. The link may have expired.');
    },
  });

  /**
   * Validate form fields
   */
  const validateForm = useCallback((): FormErrors => {
    const errors: FormErrors = {};

    // Password validation
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (password.length > 100) {
      errors.password = 'Password must be at most 100 characters';
    }

    // Confirm password validation
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  }, [password, confirmPassword]);

  /**
   * Handle field blur for validation
   */
  const handleBlur = (field: keyof TouchedFields) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errors = validateForm();
    setFormErrors(errors);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Mark all fields as touched
    setTouched({
      password: true,
      confirmPassword: true,
    });

    // Validate form
    const errors = validateForm();
    setFormErrors(errors);

    // Check if there are any errors
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Check if token exists
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    // Submit password reset
    resetPasswordMutation.mutate({
      token,
      password,
    });
  };

  /**
   * Toggle password visibility
   */
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Toggle confirm password visibility
   */
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  /**
   * Navigate to login page
   */
  const handleGoToLogin = () => {
    navigate('/login');
  };

  const isLoading = resetPasswordMutation.isPending;

  // No token provided - show error
  if (!token) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} aria-hidden="true" />
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Invalid Reset Link
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          This password reset link is invalid or has expired.
        </Typography>
        <Button variant="contained" component={RouterLink} to="/forgot-password" sx={{ mb: 2 }}>
          Request new reset link
        </Button>
        <Box>
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ArrowBackIcon fontSize="small" aria-hidden="true" />
            Back to login
          </Link>
        </Box>
      </Box>
    );
  }

  // Success state - password was reset successfully
  if (success) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircleOutlineIcon
          sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
          aria-hidden="true"
        />
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Password Reset Successful
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your password has been successfully reset. You can now sign in with your new password.
        </Typography>
        <Button variant="contained" onClick={handleGoToLogin} size="large" sx={{ height: 48 }}>
          Sign in
        </Button>
      </Box>
    );
  }

  // Form state - show password reset form
  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        Set new password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your new password below. Make sure it&apos;s at least 8 characters long.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        id="password"
        name="password"
        label="New Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onBlur={() => handleBlur('password')}
        error={touched.password && !!formErrors.password}
        helperText={
          touched.password && formErrors.password
            ? formErrors.password
            : 'Must be at least 8 characters'
        }
        required
        autoComplete="new-password"
        autoFocus
        disabled={isLoading}
        inputProps={{
          'aria-label': 'New password',
          'aria-describedby': formErrors.password ? 'password-error' : 'password-hint',
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={handleTogglePassword}
                edge="end"
                disabled={isLoading}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        id="confirmPassword"
        name="confirmPassword"
        label="Confirm New Password"
        type={showConfirmPassword ? 'text' : 'password'}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        onBlur={() => handleBlur('confirmPassword')}
        error={touched.confirmPassword && !!formErrors.confirmPassword}
        helperText={touched.confirmPassword && formErrors.confirmPassword}
        required
        autoComplete="new-password"
        disabled={isLoading}
        inputProps={{
          'aria-label': 'Confirm new password',
          'aria-describedby': formErrors.confirmPassword ? 'confirm-password-error' : undefined,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                onClick={handleToggleConfirmPassword}
                edge="end"
                disabled={isLoading}
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        aria-label={isLoading ? 'Resetting password' : 'Reset password'}
        sx={{ mb: 2, height: 48 }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            <span>Resetting password...</span>
          </Box>
        ) : (
          'Reset password'
        )}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Link
          component={RouterLink}
          to="/login"
          sx={{
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
          }}
          tabIndex={isLoading ? -1 : 0}
        >
          <ArrowBackIcon fontSize="small" aria-hidden="true" />
          Back to login
        </Link>
      </Box>
    </Box>
  );
}

export default ResetPasswordPage;
